/**
 * HVP Cascade Controller - lifecycle
 *
 * Boot, activation gate, dropdown discovery, event binding and teardown.
 * shouldActivate() MUST stay in lockstep with the theme boot-gate render
 * condition in variable.php (_hvp_mode !== disabled && count >= min).
 * bindEvents() owns the `pageshow` listener that destroy() removes.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeLifecycleMethods = {
    /**
     * Initialize controller
     *
     * @returns {boolean} Success
     */
    async init() {
        try {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CASCADE CONTROLLER INITIALIZATION START');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Controller Type: ES6 Cascade Controller (Smart Cascade)');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Product ID:', this.productId);
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Variation Count:', this.variationCount);
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Min Variations Threshold:', this.minVariations);
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Smart Cascade Enabled:', this.smartCascadeEnabled);
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Debug Mode:', this.debug);
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Attribute Order:', this.attributeOrder);
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Prefetched Options:', Object.keys(this.prefetchedOptions || {}));

            // Check if should activate
            if (!this.shouldActivate()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ACTIVATION CHECK: FAILED - Controller not activated');
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Reason: Below threshold or missing activation markers');
                this.log('Controller not activated - below threshold or missing data');
                return false;
            }

            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ACTIVATION CHECK: PASSED');

            // Find form
            this.form = document.querySelector('.variations_form');
            if (!this.form) {
                this.log('Variations form not found');
                return false;
            }

            // Find dropdowns
            this.findDropdowns();
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Found dropdowns:', this.dropdowns.size);

            if (this.dropdowns.size === 0) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ERROR: No variation dropdowns found');
                this.log('No variation dropdowns found');
                return false;
            }

            // Disable WooCommerce AJAX
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Disabling WooCommerce default handlers...');
            this.disableWooCommerceHandlers();

            // Bind events
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Binding event handlers...');
            this.bindEvents();

            // Mark form as HVP-controlled
            this.form.classList.add('hvp-enabled');
            this.form.dataset.hvpMode = 'server-cascade';
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Form marked as HVP-controlled (mode: server-cascade)');

            // Load prefetched options
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Loading prefetched options...');
            this.loadPrefetchedOptions();

            // Auto-select single options
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Auto-selecting single options...');
            await this.autoSelectSingleOptions();

            // Initialize from pre-selected default values (set by WooCommerce)
            // This syncs DOM state with controller state so UI effects are applied
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Initializing pre-selected values...');
            await this.initializePreSelectedValues();

            // Initialize UI Manager
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Initializing UI Manager...');
            this.uiManager.initialize();

            // Initial UI update (now works because this.selections is populated)
            this.scheduler.scheduleUIUpdate('highlight', 'init');
            this.scheduler.scheduleUIUpdate('progress', 'init');

            // Start health checks (after 5 second delay)
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Starting health checks...');
            setTimeout(() => this.startHealthChecks(), 5000);

            this.initialized = true;

            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CASCADE CONTROLLER INITIALIZED SUCCESSFULLY');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Method: SMART CASCADE (ES6 Controller)');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Features: Server-side cascading, Smart Cascade compatibility checking');

            this.log('Controller initialized', {
                productId: this.productId,
                variations: this.variationCount,
                attributes: this.attributeOrder
            });

            // Emit initialization event
            this.emit('initialized', {
                productId: this.productId,
                variationCount: this.variationCount
            });

            // Set legacy performance report flag for turnaround-variations compatibility
            // Turnaround checks window.haruPerformanceReport.timestamp to detect if Haru is ready
            // This handles race conditions where turnaround initializes AFTER HVP
            window.haruPerformanceReport = {
                timestamp: Date.now(),
                productId: this.productId,
                variationCount: this.variationCount,
                source: 'hvp'
            };
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Set haruPerformanceReport for turnaround compatibility');

            // Fire legacy jQuery events for turnaround-variations compatibility
            // Turnaround plugin listens for 'haru_progressive_ready' event
            this.fireLegacyReadyEvent();

            // Register with HUV if available
            this.registerWithHUV();

            return true;

        } catch (error) {
            console.error('[HVP] Initialization error:', error);
            return false;
        }
    },

    /**
     * Check if controller should activate
     */
    shouldActivate() {
        if (!this.productId || this.variationCount < this.minVariations) {
            return false;
        }

        // Check for activation markers
        return (
            document.getElementById('hvp-cascade-enabled') ||
            window.__hvp_active === true
        );
    },

    /**
     * Find all variation dropdowns
     */
    findDropdowns() {
        const selects = this.form.querySelectorAll('select[data-attribute_name]');

        selects.forEach(select => {
            const attrName = select.dataset.attribute_name;
            if (attrName) {
                // Normalize attribute name
                const normalized = this.normalizeAttributeName(attrName);
                this.dropdowns.set(normalized, select);

                this.log('Found dropdown:', normalized);
            }
        });
    },

    /**
     * Bind event handlers
     */
    bindEvents() {
        // Dropdown changes and focus events
        this.dropdowns.forEach(select => {
            select.addEventListener('change', this.handleDropdownChange);
            // Smart Cascade: Load options on focus based on above selections only
            select.addEventListener('focus', this.handleDropdownFocus);
            select.addEventListener('mousedown', this.handleDropdownFocus);
        });

        // Form reset
        this.form.addEventListener('reset', this.handleReset);

        // Clear variations link
        const clearLink = this.form.querySelector('.reset_variations');
        if (clearLink) {
            clearLink.addEventListener('click', this.handleReset);
        }

        // Back/forward navigation: browsers restore <select> values from
        // session history WITHOUT firing change events (and, for non-bfcache
        // loads, AFTER this controller has initialized). Reconcile DOM vs
        // internal state on pageshow or the restored values stay invisible to
        // the cascade forever (completeness never reached, variation never
        // resolves, pricing never appears).
        window.addEventListener('pageshow', this.handlePageShow);
    },

    /**
     * Load prefetched options
     */
    loadPrefetchedOptions() {
        if (!this.prefetchedOptions || typeof this.prefetchedOptions !== 'object') {
            return;
        }

        for (const [attr, options] of Object.entries(this.prefetchedOptions)) {
            if (Array.isArray(options) && options.length > 0) {
                const normalized = this.normalizeAttributeName(attr);
                const cacheKey = this.cache.buildKey(this.productId, {}, normalized);

                this.cache.set(cacheKey, options);
                this.updateDropdownOptions(normalized, options, false);

                this.log('Prefetched options for:', normalized, `(${options.length} options)`);
            }
        }
    },

    /**
     * Destroy controller
     */
    destroy() {
        // Stop health checks
        this.stopHealthChecks();

        // Cancel active requests
        this.api.cancelAll();

        // Clear scheduler
        if (this.scheduler) {
            this.scheduler.clear();
        }

        // Destroy UI manager (removes event listeners)
        if (this.uiManager) {
            this.uiManager.destroy();
        }

        // Remove event listeners
        this.dropdowns.forEach(select => {
            select.removeEventListener('change', this.handleDropdownChange);
            select.removeEventListener('focus', this.handleDropdownFocus);
            select.removeEventListener('mousedown', this.handleDropdownFocus);
            select.classList.remove('hvp-focus-loading');
        });

        if (this.form) {
            this.form.removeEventListener('reset', this.handleReset);
            this.form.classList.remove('hvp-enabled', 'hvp-loading');
            delete this.form.dataset.hvpMode;
        }

        // Remove restore-reconcile listener + pending passes
        window.removeEventListener('pageshow', this.handlePageShow);
        this.restoreReconcileTimers.forEach(clearTimeout);
        this.restoreReconcileTimers = [];

        // Clear cache (also clears sessionStorage)
        this.cache.clear();

        // Clear event bus
        this.eventBus.clear();

        // Reset Smart Cascade state
        this.focusLoadingAttribute = null;
        this.focusRequestCounter = 0;
        this.initialized = false;

        this.log('Controller destroyed');
    },
};

export default CascadeLifecycleMethods;
